// src/utils/claimsOutlook.js
//
// THE CLAIMS OUTLOOK — pure JavaScript, no React, no Firebase.
//
// The forecast tab's figures once the organization's own claims record is on
// file: a compensation board's sector figures (registrations, the share
// allowed, lost-time claims, return to work within three months, cost per
// claim; dictionary ids under owner 'claims', uploaded through Data Intake or
// seeded for a demo). Nothing here is modeled. Every claims figure is the
// organization's own or arithmetic on it (a year's registrations over four is
// a quarter's; lost-time claims times the share not back at three months is
// the long tail), and every strain figure is members' check-ins at the
// privacy floor (utils/earlyWarning.js). The one judgement is the reading per
// sector: where strain is up and the record has not moved, the record is read
// as lagging the check-ins and the sector is the place to act first. The
// record itself, two quarters on, is the test of that reading.
//
// Model documentation: docs/EXECUTIVE-METRICS.md ("Claims outlook on the organization's record").

import { earlyWarningRows, momentumFor, screenShare, POPULATION_LINE } from './earlyWarning';

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const val = (bag, id) => (bag && bag[id] ? num(bag[id].value) : null);
const sum = (rows, pick) => rows.reduce((acc, r) => { const v = num(pick(r)); return v === null ? acc : (acc === null ? v : acc + v); }, null);

// The claims record for one scope: a sector's figures, or the organization's
// own row. Null when no claims figure is on file for it.
export const claimsRecordFor = (bag) => {
    if (!bag) return null;
    const registered = val(bag, 'mental_stress_claims_registered');
    const per1000 = val(bag, 'mental_stress_claims_per_1000');
    const allowedShare = val(bag, 'mental_stress_allowed_share');
    const lostTime = val(bag, 'lost_time_claims');
    const rtw = val(bag, 'rtw_3_month_share');
    const duration = val(bag, 'claim_duration_days');
    const cost = val(bag, 'benefit_cost_per_claim');
    if (registered === null && per1000 === null && lostTime === null) return null;
    const first = ['mental_stress_claims_registered', 'mental_stress_claims_per_1000', 'lost_time_claims'].map((id) => bag[id]).find(Boolean) || {};
    return {
        registered, per1000, allowedShare, lostTime, rtw, duration, cost,
        // Workers covered, from the rate and the count; a quarter at the year's rate; the long tail.
        workers: registered !== null && per1000 ? Math.round((registered / per1000) * 1000) : null,
        quarter: registered !== null ? Math.round(registered / 4) : null,
        notBack: lostTime !== null && rtw !== null ? Math.round(lostTime * (1 - rtw / 100)) : null,
        source: first.source || null,
        asOf: first.asOf || null
    };
};

export const hasClaimsRecord = (live) => !!(live && live.source === 'live'
    && (claimsRecordFor(live.business) || Object.values(live.businessByGroup || {}).some((bag) => claimsRecordFor(bag))));

// The reading per sector: do the two legs agree? Strain is "up" at the
// population mark or five points up in four weeks; the record is "high" at or
// above the organization's own rate per 1,000.
export const READINGS = {
    early: { key: 'early', label: 'Early warning', line: 'Strain is up and the record has not moved yet. This is the window.' },
    both: { key: 'both', label: 'Claims following', line: 'Strain and the record agree: the claims are arriving now.' },
    record: { key: 'record', label: 'Record high, check-ins calm', line: 'A cause the check-ins do not see, or recovery under way. Read it with the sector.' },
    quiet: { key: 'quiet', label: 'Quiet', line: 'Both legs low. Hold the cadence.' }
};
const READING_ORDER = { early: 0, both: 1, record: 2, quiet: 3 };

export const readingFor = ({ now, deltaPts, per1000, orgPer1000 }) => {
    if (now === null || per1000 === null || orgPer1000 === null) return null;
    const strainUp = now >= POPULATION_LINE || (deltaPts !== null && deltaPts >= 5);
    const claimsHigh = per1000 >= orgPer1000;
    if (strainUp) return claimsHigh ? READINGS.both : READINGS.early;
    return claimsHigh ? READINGS.record : READINGS.quiet;
};

// One row per visible group that has either leg: the strain leg from the
// early-warning layer, the record leg from the organization's figures.
export const claimsOutlookRows = (live, edition) => {
    if (!live || live.source !== 'live') return [];
    const warning = Object.fromEntries(earlyWarningRows(live, edition).map((r) => [r.name, r]));
    const byGroup = live.businessByGroup || {};
    const orgRecord = claimsRecordFor(live.business);
    const orgPer1000 = orgRecord ? orgRecord.per1000 : null;
    return (live.groups || [])
        .filter((g) => g && !g.suppressed)
        .map((g) => {
            const record = claimsRecordFor(byGroup[g.name]);
            const w = warning[g.name] || null;
            const now = screenShare(g);
            const deltaPts = w ? w.momentum.deltaPts : null;
            return {
                name: g.name, responses: g.responses, now, deltaPts, weeks: w ? w.momentum.weeks : 4,
                movement: g.movement || null, driver: w ? w.driver : null, nextStep: w ? w.nextStep : null,
                record, reading: readingFor({ now, deltaPts, per1000: record ? record.per1000 : null, orgPer1000 })
            };
        })
        .filter((r) => r.record || r.now !== null)
        .sort((a, b) => ((a.reading ? READING_ORDER[a.reading.key] : 9) - (b.reading ? READING_ORDER[b.reading.key] : 9))
            || ((b.deltaPts ?? -1e9) - (a.deltaPts ?? -1e9)) || ((b.now ?? -1) - (a.now ?? -1)));
};

// Everything the forecast tab needs, once.
export const claimsOutlookSummary = (live, edition) => {
    if (!hasClaimsRecord(live)) return null;
    const rows = claimsOutlookRows(live, edition);
    const withRecord = rows.filter((r) => r.record);
    const own = claimsRecordFor(live.business);
    const registered = own && own.registered !== null ? own.registered : sum(withRecord, (r) => r.record.registered);
    const lostTime = own && own.lostTime !== null ? own.lostTime : sum(withRecord, (r) => r.record.lostTime);
    const workers = sum(withRecord, (r) => r.record.workers);
    const per1000 = own && own.per1000 !== null ? own.per1000 : (registered !== null && workers ? Math.round((registered / workers) * 10000) / 10 : null);
    const notBack = sum(withRecord, (r) => r.record.notBack);
    const source = (own && own.source) || (withRecord.find((r) => r.record.source) || { record: {} }).record.source || null;
    const asOf = (own && own.asOf) || (withRecord.find((r) => r.record.asOf) || { record: {} }).record.asOf || null;

    const q = live.quadrants || {};
    const state = { needsSupportShare: num(q.needs_support), runningHotShare: num(q.running_hot), prior: live.prior || null };
    const momentum = momentumFor(state, num(live.momentumWindowDays) || 28);
    const responded = num(live.participation && live.participation.responded);
    const share = momentum ? momentum.now : null;

    const by = (key) => rows.filter((r) => r.reading && r.reading.key === key);
    const early = by('early');
    return {
        rows,
        org: { registered, lostTime, per1000, workers, notBack, quarter: registered !== null ? Math.round(registered / 4) : null, source, asOf },
        early, both: by('both'), record: by('record'), quiet: by('quiet'),
        earlyClaims: sum(early, (r) => r.record && r.record.registered),
        earlyLostTime: sum(early, (r) => r.record && r.record.lostTime),
        responded,
        screenShare: share,
        screenCount: share !== null && responded !== null ? Math.round(share * responded) : null,
        deltaPts: momentum ? momentum.deltaPts : null,
        weeks: momentum ? momentum.weeks : 4,
        newlyPositive: live.movement ? num(live.movement.newlyPositive) : null
    };
};
